import axios from 'axios';
import { NaatKhawan, AudioFile, APIResponse, PaginatedResponse, PopularAudiosPaginatedResponse } from '../types';
import { API_BASE_URL } from '../config';
import { config } from '../config/environment';

const api = axios.create({
  baseURL: API_BASE_URL, // Already includes /api from config/index.ts
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: config.corsEnabled,
});

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);

export const naatKhawanService = {
  // Get all NaatKhawans with pagination
  getAll: async (page = 1, limit = 20, search?: string): Promise<APIResponse<PaginatedResponse<NaatKhawan>>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }

    const response = await api.get(`/naatkhawans?${params.toString()}`);
    return response.data;
  },

  // Get single NaatKhawan by ID
  getById: async (id: number): Promise<APIResponse<NaatKhawan>> => {
    const response = await api.get(`/naatkhawans/${id}`);
    return response.data;
  },

  // Get NaatKhawans alphabetically
  getAlphabetical: async (): Promise<APIResponse<NaatKhawan[]>> => {
    const response = await api.get('/naatkhawans?sort=name');
    return response.data;
  },

  // Get NaatKhawans ordered by audio count (highest first)
  getByAudioCount: async (limit = 10): Promise<APIResponse<NaatKhawan[]>> => {
    const response = await api.get(`/naatkhawans?sort=audio_count&limit=${limit}`);
    return response.data;
  },

  // Get audio files by NaatKhawan ID
  getAudios: async (id: number, page = 1, limit = 20, initial?: string): Promise<APIResponse<PaginatedResponse<AudioFile>>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (initial) params.append('initial', initial);
    const response = await api.get(`/naatkhawans/${id}/audios?${params.toString()}`);
    return response.data;
  },
};

export const audioService = {
  // Get all audio files
  getAll: async (page = 1, limit = 20, search?: string): Promise<APIResponse<PaginatedResponse<AudioFile>>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }

    const response = await api.get(`/audios?${params.toString()}`);
    return response.data;
  },

  // Get single audio file by ID
  getById: async (id: number): Promise<APIResponse<AudioFile>> => {
    const response = await api.get(`/audios/${id}`);
    return response.data;
  },

  // Track audio play
  trackPlay: async (id: number): Promise<APIResponse<{ playCount: number }>> => {
    const response = await api.post(`/audios/${id}/play`);
    return response.data;
  },

  // Get popular/most played audios
  getPopular: async (limit = 10): Promise<APIResponse<AudioFile[]>> => {
    const response = await api.get(`/audios/popular?limit=${limit}`);
    return response.data;
  },

  // Get popular/most played audios with pagination
  getPopularPaginated: async (page = 1, limit = 20): Promise<PopularAudiosPaginatedResponse> => {
    const response = await api.get(`/audios/popular?page=${page}&limit=${limit}`);
    return response.data;
  },

  // Get recent uploads
  getRecent: async (limit = 10): Promise<APIResponse<AudioFile[]>> => {
    const response = await api.get(`/audios/recent?limit=${limit}`);
    return response.data;
  },

  // Get recent uploads with pagination
  getRecentPaginated: async (page = 1, limit = 20): Promise<PopularAudiosPaginatedResponse> => {
    const response = await api.get(`/audios/recent?page=${page}&limit=${limit}`);
    return response.data;
  },

  // Get random audio files
  getRandom: async (limit = 20): Promise<APIResponse<AudioFile[]>> => {
    const response = await api.get(`/audios/random?limit=${limit}`);
    return response.data;
  },
};

// Admin API Services
export const adminService = {
  // Naats CRUD
  createAudio: async (data: FormData): Promise<APIResponse<AudioFile>> => {
    try {
      // Don't manually set Content-Type for FormData - axios will set it automatically with boundary
      console.log('Sending FormData to /admin/audios');
      const response = await api.post('/admin/audios', data);
      console.log('Response from server:', response.data);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      console.error('Create audio error:', error.response?.data || error);
      console.error('Full error object:', error);
      console.error('Response status:', error.response?.status);
      return {
        success: false,
        data: null as any,
        error: errorMessage
      };
    }
  },

  updateAudio: async (id: number, data: Partial<AudioFile>): Promise<APIResponse<AudioFile>> => {
    try {
      const response = await api.put(`/admin/audios/${id}`, data);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      return { 
        success: false, 
        data: null as any, 
        error: errorMessage 
      };
    }
  },

  deleteAudio: async (id: number): Promise<APIResponse<void>> => {
    try {
      const response = await api.delete(`/admin/audios/${id}`);
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      return { 
        success: false, 
        data: null as any, 
        error: errorMessage 
      };
    }
  },

  // NaatKhawans CRUD
  createNaatKhawan: async (data: Partial<NaatKhawan>): Promise<APIResponse<NaatKhawan>> => {
    try {
      // Sanitize data to avoid ModSecurity triggers using Base64 encoding
      const sanitizedData = {
        ...data,
        // Base64 encode text fields that might trigger ModSecurity
        urduName: data.urduName ? btoa(unescape(encodeURIComponent(data.urduName))) : data.urduName,
        bio: data.bio ? btoa(unescape(encodeURIComponent(data.bio))) : data.bio,
        name: data.name ? btoa(unescape(encodeURIComponent(data.name))) : data.name,
        birthPlace: data.birthPlace ? btoa(unescape(encodeURIComponent(data.birthPlace))) : data.birthPlace,
        // Add a flag to indicate base64 encoding
        _encoded: true
      };

      console.log('Creating NaatKhawan with data:', data);
      console.log('Sanitized data:', sanitizedData);

      const response = await api.post('/admin/naatkhawans', sanitizedData);
      return response.data;
    } catch (error: any) {
      console.error('Create NaatKhawan error:', error);

      // Handle ModSecurity specifically
      if (error.response?.status === 403) {
        const errorMessage = error.response?.data?.message ||
                           'Request blocked by security rules. This may be due to special characters in text fields. Try simplifying the content.';
        return {
          success: false,
          data: null as any,
          error: errorMessage,
          message: 'Security Error: ' + errorMessage
        };
      }

      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      return {
        success: false,
        data: null as any,
        error: errorMessage,
        message: errorMessage
      };
    }
  },

  updateNaatKhawan: async (id: number, data: Partial<NaatKhawan>): Promise<APIResponse<NaatKhawan>> => {
    try {
      // Sanitize data to avoid ModSecurity triggers using Base64 encoding
      const sanitizedData = {
        ...data,
        // Base64 encode text fields that might trigger ModSecurity
        urduName: data.urduName ? btoa(unescape(encodeURIComponent(data.urduName))) : data.urduName,
        bio: data.bio ? btoa(unescape(encodeURIComponent(data.bio))) : data.bio,
        name: data.name ? btoa(unescape(encodeURIComponent(data.name))) : data.name,
        birthPlace: data.birthPlace ? btoa(unescape(encodeURIComponent(data.birthPlace))) : data.birthPlace,
        // Add a flag to indicate base64 encoding
        _encoded: true
      };

      console.log('Updating NaatKhawan with data:', data);
      console.log('Sanitized data:', sanitizedData);

      const response = await api.put(`/admin/naatkhawans/${id}`, sanitizedData);
      return response.data;
    } catch (error: any) {
      console.error('Update NaatKhawan error:', error);

      // Handle ModSecurity specifically
      if (error.response?.status === 403) {
        const errorMessage = error.response?.data?.message ||
                           'Request blocked by security rules. This may be due to special characters in text fields. Try simplifying the content.';
        return {
          success: false,
          data: null as any,
          error: errorMessage,
          message: 'Security Error: ' + errorMessage
        };
      }

      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Unknown error';
      return {
        success: false,
        data: null as any,
        error: errorMessage,
        message: errorMessage
      };
    }
  },

  deleteNaatKhawan: async (id: number): Promise<APIResponse<void>> => {
    const response = await api.delete(`/admin/naatkhawans/${id}`);
    return response.data;
  },
};

// Favorite service for managing favorite naats
export const favoriteService = {
  getFavorites: async (): Promise<APIResponse<AudioFile[]>> => {
    try {
      // For now, we'll use localStorage to store favorites
      // In production, this would be an API call to the backend
      const favorites = localStorage.getItem('naatcity-favorites');
      const favoriteIds = favorites ? JSON.parse(favorites) : [];

      // Return the favorite IDs for now
      // In production, fetch full audio data from backend
      return {
        success: true,
        data: favoriteIds.map((id: number) => ({ id }))
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        error: 'Failed to load favorites'
      };
    }
  },

  addFavorite: async (audioId: number): Promise<APIResponse<void>> => {
    try {
      const favorites = localStorage.getItem('naatcity-favorites');
      const favoriteIds = favorites ? JSON.parse(favorites) : [];

      if (!favoriteIds.includes(audioId)) {
        favoriteIds.push(audioId);
        localStorage.setItem('naatcity-favorites', JSON.stringify(favoriteIds));
      }

      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        data: undefined,
        error: 'Failed to add favorite'
      };
    }
  },

  removeFavorite: async (audioId: number): Promise<APIResponse<void>> => {
    try {
      const favorites = localStorage.getItem('naatcity-favorites');
      const favoriteIds = favorites ? JSON.parse(favorites) : [];

      const index = favoriteIds.indexOf(audioId);
      if (index > -1) {
        favoriteIds.splice(index, 1);
        localStorage.setItem('naatcity-favorites', JSON.stringify(favoriteIds));
      }

      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        data: undefined,
        error: 'Failed to remove favorite'
      };
    }
  },

  isFavorite: async (audioId: number): Promise<boolean> => {
    try {
      const favorites = localStorage.getItem('naatcity-favorites');
      const favoriteIds = favorites ? JSON.parse(favorites) : [];
      return favoriteIds.includes(audioId);
    } catch (error) {
      return false;
    }
  }
};

export default api;